import { logMessage } from '@/lib/functions/logMessage';

export interface ProductStockState {
  available: boolean;
  inStock: boolean;
  lowStock: boolean;
  quantityAvailable?: number
  message: string;
}

/**
 * checkProductStock Function
 *
 * This function checks the stock availability of a product using your internal
 * Next.js API route: /api/inventory/[variantCode]/.
 * Evaluates `lowStock` based on totalAvailable vs lowStockThreshold.
 *
 * @param {string} variantCode - The variant code identifier for the product.
 * @param {number} lowStockThreshold - The quantity threshold below which stock is considered "low" (default: 10).
 *
 * @returns {Promise<ProductStockState>} Stock availability status.
 */
export const checkProductStock = async (
  variantCode: string | undefined,
  lowStockThreshold: number = 5
): Promise<ProductStockState> => {
  if (variantCode == null || variantCode === '') {
    logMessage('Error: Missing Variant Code', 'error');
    return { available: false, inStock: false, lowStock: false, quantityAvailable: 0, message: 'Unavailable' };
  }

  try {
    const apiUrl = `/api/products/stock/${variantCode}/`;

    const response = await fetch(apiUrl, {
      method: 'GET',
      cache: 'no-cache',
    });

    if (!response.ok) {
      logMessage(`Inventory API failed for Variant Code ${variantCode}`, 'error');
      return { available: false, inStock: false, lowStock: false, quantityAvailable: 0, message: 'Unavailable' };
    }

    const data = await response.json();

    const totalAvailable = data.available ?? 0;
    const inStock = totalAvailable > 0;
    const lowStock = inStock && totalAvailable < lowStockThreshold;

    return {
      available: true,
      inStock,
      lowStock,
      quantityAvailable: totalAvailable,
      message: inStock
        ? lowStock
          ? 'Low Stock' //totalAvailable to check amount
          : 'In Stock'
        : 'Out of Stock',
    };
  } catch (error) {
    logMessage(`checkProductStock API error: ${(error as Error).message}`, 'error');
    return { available: false, inStock: false, lowStock: false, quantityAvailable: 0, message: 'Unavailable' };
  }
};
